import { useState } from 'react';
import type { BillingConfig, CartItem } from '@cartiq/shared';
import { SUPPORTED_COUNTRIES, computeBilling, isValidEmail } from '@cartiq/shared';
import type { CheckoutDetails } from '../hooks/useChat';
import BillingSummary from './BillingSummary';

interface Props {
  cart: CartItem[];
  onCheckout: (details: CheckoutDetails) => void;
  onUpdateQuantity: (productId: string, quantity: number, variantId?: string) => void;
  onRemove: (productId: string, variantId?: string) => void;
  /** Store delivery/tax/fee rules, used to show the order breakdown. */
  billing?: BillingConfig | null;
  /** Centering wrapper so the cart aligns with the chat column on desktop. */
  contentClass?: string;
}

function formatPrice(price: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
    }).format(price);
  } catch {
    return `${currency} ${price.toFixed(2)}`;
  }
}

export function CartSummary({ cart, onCheckout, onUpdateQuantity, onRemove, billing, contentClass = 'w-full' }: Props) {
  const [open, setOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [country, setCountry] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [deliveryLocation, setDeliveryLocation] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [deliveryInstructions, setDeliveryInstructions] = useState('');
  const [error, setError] = useState<string | null>(null);

  const deliveryLocations = billing?.delivery?.enabled ? billing.delivery.locations ?? [] : [];
  const requiresShipping = cart.some(
    (item) => (item.fulfillmentType ?? 'physical') === 'physical',
  );
  const effectiveSlug = deliveryLocation || undefined;
  const breakdown = computeBilling(cart, billing ?? null, {
    deliveryLocationSlug: effectiveSlug,
  });
  const blocked = Boolean(breakdown.belowMinimum || breakdown.issues?.length);

  function submitCheckout() {
    if (!country) {
      setError('Please select your country.');
      return;
    }
    if (requiresShipping && deliveryLocations.length > 0 && !deliveryLocation) {
      setError('Please choose a delivery destination.');
      return;
    }
    if (
      requiresShipping &&
      (!name.trim() || !phone.trim() || !addressLine1.trim() || !city.trim())
    ) {
      setError('Please enter the recipient name, phone, street address, and city.');
      return;
    }
    if (!isValidEmail(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    if (breakdown.issues?.length) {
      setError(breakdown.issues[0].message);
      return;
    }
    if (breakdown.belowMinimum) {
      setError(`Minimum order is ${formatPrice(breakdown.belowMinimum.minimum, breakdown.currency)}.`);
      return;
    }
    setError(null);
    onCheckout({
      customerEmail: email.trim(),
      customerName: name.trim() || undefined,
      customerPhone: phone.trim() || undefined,
      customerCountry: country,
      deliveryLocationSlug: effectiveSlug,
      fulfillmentMethod: requiresShipping ? 'shipping' : undefined,
      deliveryAddress: requiresShipping
        ? {
            line1: addressLine1.trim(),
            line2: addressLine2.trim() || undefined,
            city: city.trim(),
            region: region.trim() || undefined,
            postalCode: postalCode.trim() || undefined,
            country,
          }
        : undefined,
      deliveryInstructions: deliveryInstructions.trim() || undefined,
    });
  }

  if (cart.length === 0) return null;

  const inputClass =
    'w-full rounded-sm border border-border bg-white px-3 py-2 text-sm text-primary outline-none transition-colors focus:border-[var(--brand-accent)]';

  const currency = cart[0]?.currency ?? 'USD';
  const count = cart.reduce((n, i) => n + i.quantity, 0);
  // Billed total includes delivery/tax/fees so the buttons match what's charged.
  const total = breakdown.total;

  return (
    <div className="shrink-0 border-t border-border bg-white">
      <div className={contentClass}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-2.5 text-left"
      >
        <div className="flex items-center gap-2">
          <div className="relative">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--brand-accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
              <path d="M3 6h18M16 10a4 4 0 0 1-8 0" />
            </svg>
            <span
              className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white"
              style={{ backgroundColor: 'var(--brand-accent)' }}
            >
              {count}
            </span>
          </div>
          <span className="text-xs font-semibold text-primary">
            {count} item{count === 1 ? '' : 's'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-primary">{formatPrice(total, currency)}</span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className={`text-mid transition-transform ${open ? 'rotate-180' : ''}`}
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>
      </button>

      <div
        className="overflow-y-auto overscroll-contain transition-[max-height] duration-300 ease-out"
        style={{ maxHeight: open ? 'min(900px, 60dvh)' : 0 }}
      >
        <div className="scroll-smooth-y max-h-44 overflow-y-auto px-4">
          {cart.map((item) => (
            <div
              key={`${item.productId}-${item.variantId ?? ''}`}
              className="flex items-center justify-between gap-2 border-b border-border/60 py-2 last:border-0"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium text-primary">{item.productName}</p>
                <span className="text-xs font-semibold text-primary">
                  {formatPrice(item.price * item.quantity, item.currency)}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <div className="flex items-center rounded-sm border border-border">
                  <button
                    onClick={() =>
                      onUpdateQuantity(item.productId, item.quantity - 1, item.variantId)
                    }
                    aria-label={`Decrease ${item.productName} quantity`}
                    className="flex h-8 w-8 items-center justify-center text-mid transition-colors hover:bg-surface active:scale-90"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                      <path d="M5 12h14" />
                    </svg>
                  </button>
                  <span className="min-w-5 text-center text-xs font-semibold text-primary" aria-label={`Quantity ${item.quantity}`}>
                    {item.quantity}
                  </span>
                  <button
                    onClick={() =>
                      onUpdateQuantity(item.productId, item.quantity + 1, item.variantId)
                    }
                    aria-label={`Increase ${item.productName} quantity`}
                    className="flex h-8 w-8 items-center justify-center text-mid transition-colors hover:bg-surface active:scale-90"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  </button>
                </div>
                <button
                  onClick={() => onRemove(item.productId, item.variantId)}
                  aria-label={`Remove ${item.productName}`}
                  className="flex h-8 w-8 items-center justify-center rounded-sm text-mid transition-colors hover:bg-error/10 hover:text-error active:scale-90"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="p-3">
          {!showForm ? (
            <button
              onClick={() => setShowForm(true)}
              className="w-full rounded-sm px-4 py-2.5 text-sm font-semibold text-white transition-transform active:scale-[0.98]"
              style={{ backgroundColor: 'var(--brand-accent)' }}
            >
              Checkout · {formatPrice(total, currency)}
            </button>
          ) : (
            <div className="flex flex-col gap-2">
              {requiresShipping && deliveryLocations.length > 0 && (
                <select
                  value={deliveryLocation}
                  onChange={(e) => setDeliveryLocation(e.target.value)}
                  required
                  className={inputClass}
                  aria-label="Delivery destination"
                >
                  <option value="" disabled>
                    Where should we deliver?
                  </option>
                  {deliveryLocations.map((l) => (
                    <option key={l.slug} value={l.slug}>
                      {l.location}
                    </option>
                  ))}
                </select>
              )}
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                required
                className={inputClass}
                aria-label="Country"
              >
                <option value="" disabled>
                  Select your country…
                </option>
                {SUPPORTED_COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Email address"
                autoComplete="email"
                className={inputClass}
                aria-label="Email address"
              />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={requiresShipping ? 'Recipient full name' : 'Full name (optional)'}
                autoComplete="name"
                className={inputClass}
                aria-label="Full name"
              />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={requiresShipping ? 'Recipient phone number' : 'Phone (optional)'}
                autoComplete="tel"
                className={inputClass}
                aria-label="Phone number"
              />
              {requiresShipping && (
                <>
                  <input
                    value={addressLine1}
                    onChange={(e) => setAddressLine1(e.target.value)}
                    placeholder="Street address"
                    autoComplete="address-line1"
                    className={inputClass}
                    aria-label="Street address"
                  />
                  <input
                    value={addressLine2}
                    onChange={(e) => setAddressLine2(e.target.value)}
                    placeholder="Apartment, suite, etc. (optional)"
                    autoComplete="address-line2"
                    className={inputClass}
                    aria-label="Address line 2"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="City"
                      autoComplete="address-level2"
                      className={inputClass}
                      aria-label="City"
                    />
                    <input
                      value={region}
                      onChange={(e) => setRegion(e.target.value)}
                      placeholder="State / region"
                      autoComplete="address-level1"
                      className={inputClass}
                      aria-label="State or region"
                    />
                  </div>
                  <input
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    placeholder="Postal code (optional)"
                    autoComplete="postal-code"
                    className={inputClass}
                    aria-label="Postal code"
                  />
                  <textarea
                    value={deliveryInstructions}
                    onChange={(e) => setDeliveryInstructions(e.target.value)}
                    placeholder="Delivery instructions (optional)"
                    className={inputClass}
                    aria-label="Delivery instructions"
                    rows={2}
                  />
                </>
              )}

              <div className="rounded-sm bg-surface p-3">
                <BillingSummary cart={cart} billing={billing} deliveryLocationSlug={effectiveSlug} />
              </div>

              {error && <p className="text-xs font-medium text-error">{error}</p>}

              <button
                onClick={submitCheckout}
                disabled={blocked}
                className="w-full rounded-sm px-4 py-2.5 text-sm font-semibold text-white transition-transform active:scale-[0.98] disabled:opacity-60"
                style={{ backgroundColor: 'var(--brand-accent)' }}
              >
                Pay · {formatPrice(total, currency)}
              </button>
              <p className="text-center text-[11px] text-mid">
                Your country determines the secure payment method.
              </p>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}

export default CartSummary;
